import { useState } from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, Truck, Lock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { useSiteMedia } from "@/lib/siteImages";

export default function Footer() {
  const { t, lang } = useLanguage();
  const media = useSiteMedia();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [welcomeCode, setWelcomeCode] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const res = await api.newsletter(email, lang);
      setStatus("ok");
      setWelcomeCode(res.code || "");
      setEmail("");
    } catch {
      setStatus("err");
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy text-white noise-bg">
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-baseline gap-2">
              {media("logo") && (
                <img src={media("logo")} alt="Antalia Pharma" data-testid="footer-brand-logo" className="h-10 w-auto self-center object-contain" />
              )}
              <span className="font-display text-3xl font-medium">Antalia</span>
              <span className="label-luxe text-gold">Pharma</span>
            </div>
            <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-white/50">{t.foot.tag}</p>

            <form onSubmit={subscribe} className="mt-10 max-w-sm" data-testid="newsletter-form">
              <p className="label-luxe mb-4 text-gold">{t.news.title}</p>
              <div className="flex items-end gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.news.ph}
                  data-testid="newsletter-email-input"
                  className="input-underline"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit-button"
                  className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-white"
                >
                  {t.news.btn}
                </button>
              </div>
              {status === "ok" && (
                <>
                  <p data-testid="newsletter-success" className="mt-3 text-xs text-sage">{t.news.ok}</p>
                  {welcomeCode && (
                    <p data-testid="newsletter-code" className="mt-2 text-xs font-semibold tracking-wide text-gold">{t.news.code.replace("WELCOME10", welcomeCode)}</p>
                  )}
                </>
              )}
              {status === "err" && (
                <p data-testid="newsletter-error" className="mt-3 text-xs text-red-300">{t.news.err}</p>
              )}
            </form>
          </div>

          <div className="lg:col-span-2">
            <p className="label-luxe mb-6 text-white/40">{t.foot.shop}</p>
            <ul className="space-y-3 text-sm font-light text-white/60">
              <li><Link data-testid="footer-shop-all" to="/shop" className="transition-colors hover:text-gold">{t.nav.shop}</Link></li>
              <li><Link data-testid="footer-shop-immune" to="/shop?cat=immune" className="transition-colors hover:text-gold">{t.cats.immune_n}</Link></li>
              <li><Link data-testid="footer-shop-focus" to="/shop?cat=focus" className="transition-colors hover:text-gold">{t.cats.focus_n}</Link></li>
              <li><Link data-testid="footer-shop-vitality" to="/shop?cat=vitality" className="transition-colors hover:text-gold">{t.cats.vitality_n}</Link></li>
              <li><Link data-testid="footer-shop-relaxation" to="/shop?cat=relaxation" className="transition-colors hover:text-gold">{t.cats.relaxation_n}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="label-luxe mb-6 text-white/40">{t.foot.company}</p>
            <ul className="space-y-3 text-sm font-light text-white/60">
              <li><Link data-testid="footer-about" to="/about" className="transition-colors hover:text-gold">{t.nav.about}</Link></li>
              <li><Link data-testid="footer-science" to="/science" className="transition-colors hover:text-gold">{t.nav.science}</Link></li>
              <li><Link data-testid="footer-blog" to="/blog" className="transition-colors hover:text-gold">{t.nav.blog}</Link></li>
              <li><Link data-testid="footer-lookbook" to="/lookbook" className="transition-colors hover:text-gold">{t.nav.lookbook}</Link></li>
              <li><Link data-testid="footer-track" to="/track" className="transition-colors hover:text-gold">{t.track.kicker}</Link></li>
              <li><Link data-testid="footer-contact" to="/contact" className="transition-colors hover:text-gold">{t.nav.contact}</Link></li>
              <li><Link data-testid="footer-legal-privacy" to="/legal/privacy" className="transition-colors hover:text-gold">{t.legal.privacy}</Link></li>
              <li><Link data-testid="footer-legal-terms" to="/legal/terms" className="transition-colors hover:text-gold">{t.legal.terms}</Link></li>
              <li><Link data-testid="footer-legal-returns" to="/legal/returns" className="transition-colors hover:text-gold">{t.legal.returns}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="label-luxe mb-6 text-white/40">{t.foot.contact}</p>
            <ul className="space-y-3 text-sm font-light text-white/60">
              <li>30 Agias Irinis Str.</li>
              <li>Marousi 15126, Athens, Greece</li>
              <li>
                <a data-testid="footer-phone" href="tel:+302108100000" className="transition-colors hover:text-gold">
                  +30 210 810 0000
                </a>
              </li>
              <li>
                <a data-testid="footer-email" href="mailto:info@antalia-pharma.com" className="transition-colors hover:text-gold">
                  info@antalia-pharma.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 md:grid-cols-4">
          {[
            { icon: Award, label: t.foot.gmp },
            { icon: ShieldCheck, label: t.foot.eu },
            { icon: Truck, label: t.foot.shipping },
            { icon: Lock, label: t.foot.secure },
          ].map(({ icon: Icon, label }, i) => (
            <div key={i} data-testid={`trust-seal-${i}`} className="flex items-center gap-3">
              <Icon size={18} strokeWidth={1.25} className="text-gold" />
              <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-white/50">{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-16 select-none overflow-hidden text-center">
          <p className="font-display text-6xl font-light italic leading-none text-white/[0.07] lg:text-8xl">
            Antalia Pharma
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-[11px] font-light tracking-wide text-white/35">{t.foot.rights}</p>
          <p className="font-display text-sm italic text-white/35">www.antalia-pharma.com</p>
        </div>
      </div>
    </footer>
  );
}
